import React from 'react'
import { PiMagnifyingGlass } from 'react-icons/pi'
import { FaMapMarkerAlt, FaGlobe } from 'react-icons/fa'
import { AiOutlineLoading3Quarters, AiFillProduct } from 'react-icons/ai'
import { cn } from 'components/UI/cn'
import { MIN_SEARCH_CHARS } from './constants'
import { SearchType } from './hooks/useUnifiedSearchAPI'

interface UnifiedSearchInputProps {
  searchTerm: string
  setSearchTerm: (term: string) => void
  onSubmit: (e: React.FormEvent) => void
  searchType?: SearchType | null
  isLoading?: boolean
}

export function UnifiedSearchInput({
  searchTerm,
  setSearchTerm,
  onSubmit,
  searchType,
  isLoading,
}: UnifiedSearchInputProps) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      onSubmit(e as unknown as React.FormEvent)
    }
  }

  const getSearchTypeIndicator = () => {
    // Don't show anything if there's no search term
    if (!searchTerm || searchTerm.length < MIN_SEARCH_CHARS) return null

    // Show loading indicator while search is in progress (only for potential address/domain searches)
    if (isLoading && !searchType) {
      return (
        <div
          className={`
            absolute right-3 top-1/2 transform -translate-y-1/2
            flex items-center justify-center
            text-gray-400
            transition-all duration-200 ease-in-out
          `}
        >
          <AiOutlineLoading3Quarters className="h-4 w-4 animate-spin" />
        </div>
      )
    }

    // Only show badges for address, domain, and SKU searches
    // Name searches don't get a badge since they search everything
    if (!searchType || searchType === 'name') return null

    const typeConfig: Record<
      string,
      {
        icon: typeof FaMapMarkerAlt | typeof FaGlobe | typeof AiFillProduct
        label: string
        bgColor: string
        textColor: string
        borderColor: string
      }
    > = {
      address: {
        icon: FaMapMarkerAlt,
        label: 'Address Search',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        borderColor: 'border-blue-300',
      },
      domain: {
        icon: FaGlobe,
        label: 'Domain Search',
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-700',
        borderColor: 'border-purple-300',
      },
      sku: {
        icon: AiFillProduct,
        label: 'SKU Search',
        bgColor: 'bg-green-100',
        textColor: 'text-green-700',
        borderColor: 'border-green-300',
      },
    }

    const config = typeConfig[searchType]
    if (!config) return null

    const Icon = config.icon

    return (
      <div
        className={`
          absolute right-3 top-1/2 transform -translate-y-1/2
          flex items-center gap-1.5 px-2.5 py-1 rounded-full
          ${config.bgColor} ${config.textColor} ${config.borderColor}
          border text-xs font-medium
          transition-all duration-200 ease-in-out
        `}
      >
        <Icon className="h-3 w-3" />
        <span>{config.label}</span>
      </div>
    )
  }

  const rightPadding =
    (searchType === 'address' ||
      searchType === 'domain' ||
      searchType === 'sku') &&
    searchTerm &&
    searchTerm.length >= MIN_SEARCH_CHARS
      ? 'pr-32'
      : isLoading &&
          !searchType &&
          searchTerm &&
          searchTerm.length >= MIN_SEARCH_CHARS
        ? 'pr-10'
        : 'pr-4'

  // DS2026 "new search bar" — render the same data-search-shell markers as
  // TableSearch so the shared `.fb-plain-links [data-search-shell]` chrome in
  // main.css styles it identically (40px shell, brand-green icon, subtle
  // shadow). Call sites must mount this inside a plain TableLinkStyleProvider.
  return (
    <div
      data-search-shell="true"
      style={{ position: 'relative' }}
      className="w-full"
    >
      <div
        data-search-icon="true"
        className="pointer-events-none absolute left-0 top-0 flex h-full items-center pl-3 text-lg"
      >
        <PiMagnifyingGlass />
      </div>
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search across all target types by name, domain, address or SKU"
        className={cn('form-control', 'w-full pl-9', rightPadding)}
        autoFocus
      />
      {getSearchTypeIndicator()}
    </div>
  )
}
